import { Component, ElementRef, OnInit, ViewChild, computed, input, output, signal } from '@angular/core';
import { MediaService } from '../../content';
import { Language } from '../language.model';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RangeInputStep } from '../../forms';
import { LanguageSkill } from '../language-skill.model';
import { provideTranslocoScope, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LanguageService } from '../language.service';
import { FlagExtraLite } from '../../flags';
import { ProportionInputItem } from '../../surveys/proportion-input-item.model';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { SurveyProportionEditCardModule } from '../../surveys/survey-proportion-edit-card/survey-proportion-edit-card.module';

@Component({
  selector: 'multisite-user-language-editor',
  templateUrl: './user-language-editor.component.html',
  styleUrl: './user-language-editor.component.scss',

  imports: [
    ReactiveFormsModule,
    TypeaheadModule,
    TranslocoDirective,
    SurveyProportionEditCardModule,
  ],
  providers: [
    provideTranslocoScope('language'),
    provideTranslocoScope('survey'),
  ]
})
export class UserLanguageEditorComponent implements OnInit {
  @ViewChild('languageInput') languageInput?: ElementRef<HTMLInputElement>;

  allLanguages = input.required<Language[]>();
  myLanguages = input<Language[] | undefined>(undefined);
  error = input<string|{message:string,meta:any}|undefined>(undefined);
  loading = input<boolean>();
  onSaveLanguage = output<LanguageSkill>();
  onDeleteLanguage = output<Language>();
  languageAddForm : FormGroup | undefined;
  availableLanguages = computed(() => {
    const myLanguages: Language[] = this.myLanguages() ?? [];
    if (!myLanguages.length) {
      return this.allLanguages();
    }
      return this.allLanguages().filter(language => !(myLanguages.some(myLanguage => myLanguage.id === language.id)));
  });
  newLanguage = signal<Language | null>(null);
  myLanguagesAndNewLanguage = computed<ProportionInputItem[]>(() => {
    const myLanguages: Language[] = this.myLanguages() ?? [];
    const newLanguage = this.newLanguage();
    const languages: Language[] =
      newLanguage && !myLanguages.some((language) => language.id === newLanguage.id)
        ? [newLanguage, ...myLanguages]
        : myLanguages;

    return languages.map((language) => ({
      id: language.id,
      relatedObject: language,
      numberValue: language.skill?.level ?? 0,
      textValue: language.skill?.description ?? '',
    }));
  });
  // myLanguageUpdateCount = signal<number>(0);


  constructor(
    private mediaService : MediaService,
    private translocoService : TranslocoService,
    private languageService : LanguageService,
  ) {
    
  }

  get languageSkillLevelRangeInputSteps() : RangeInputStep[]{
    return this.languageService.languageSkillLevelRangeInputSteps;
  };

  instantiateNewLanguage(){
    const name = this.translocoService.translate('common.add_a_language');
    const flagUrl = this.mediaService.getFlagUrlFromHash('missing','w_32,c_fill,ar_1:1,r_max/',true,'.png');
    this.newLanguage.set(new Language(0,'',name,new FlagExtraLite('',''),flagUrl));
  }

  getFlagUrlFromHash(hash:string,transformations : string = ''){
    return this.mediaService.getFlagUrlFromHash(hash,transformations,true,'.png');
  }

  onNewLanguageSelect(event : {item: Language}){
    const language = event.item ? event.item : null;
    this.newLanguage.set(language);

    this.languageInput?.nativeElement.blur();
    if (this.languageAddForm) {
      this.languageAddForm.reset();
    }
  }

  onSubmit(languageSkillValues: ProportionInputItem){
    if(!languageSkillValues){ return; }
    const language = this.allLanguages()?.find(lang => lang.id === languageSkillValues.id);
    if(!language){ return; }
    const level = +languageSkillValues.numberValue;
    const description = languageSkillValues.textValue;
    const languageSkill = new LanguageSkill(language.id, level, description); 
    this.onSaveLanguage.emit(languageSkill);
  }

  onLanguageRemove(languageItem : ProportionInputItem | undefined){
    if (!languageItem) {
      return;
    }
    const translation = this.translocoService.translate('common.permanent_are_you_sure');
    if (confirm(translation)) {
      if(languageItem.id === this.newLanguage()?.id){
        this.newLanguage.set(null);
        return;
      }
      const language = this.allLanguages()?.find(lang => lang.id === languageItem.id);
      if(!language){ return; }
      this.onDeleteLanguage.emit(language);
    }
  }
  initialiseForm(){
    this.languageAddForm = new FormGroup({
      language: new FormControl(null, Validators.required),
    });
  }

  ngOnInit(){
    this.initialiseForm();
  }


}
